import {
  contentChildren,
  Directive,
  effect,
  forwardRef,
  signal,
  untracked,
} from '@angular/core';
import { MultiSelectCheckboxComponent } from '@shared/components/multi-select/multi-select-checkbox/multi-select-checkbox.component';

@Directive({
  selector: '[appMultipleSelectGroup]',
  standalone: true,
})
export class MultipleSelectGroupDirective {
  public checkboxes = contentChildren<MultiSelectCheckboxComponent>(
    forwardRef(() => MultiSelectCheckboxComponent)
  );

  private searchValue = signal<string>('');

  constructor() {
    // Update the group when the group children are checked or unchecked
    effect(() => {
      const array = [...this.checkboxes()];
      array.shift();

      const groupCheckbox = this.checkboxes()[0];
      if (!groupCheckbox) {
        return;
      }

      const groupChecked = untracked(() => groupCheckbox.isChecked());
      const childCheckboxes = this.searchValue()
        ? this.getSearchSelectableChildCheckboxes(array)
        : this.getChildCheckboxes(array);

      if (!groupChecked && this.isGroupSelected(childCheckboxes)) {
        groupCheckbox.isChecked.set(true);
      } else if (groupChecked && !this.isGroupSelected(childCheckboxes)) {
        groupCheckbox.isChecked.set(false);
      }
    });
  }

  private isGroupSelected(array: MultiSelectCheckboxComponent[]): boolean {
    return (
      array.length > 0 &&
      array.every((element: MultiSelectCheckboxComponent) =>
        element.isChecked()
      )
    );
  }

  private getChildCheckboxes(
    checkboxes: readonly MultiSelectCheckboxComponent[] = this.checkboxes()
  ): MultiSelectCheckboxComponent[] {
    const groupCheckbox = this.checkboxes()[0];
    if (!groupCheckbox) {
      return [];
    }

    return checkboxes.filter((checkbox) => checkbox !== groupCheckbox);
  }

  private getSearchSelectableChildCheckboxes(
    checkboxes: readonly MultiSelectCheckboxComponent[] = this.checkboxes()
  ): MultiSelectCheckboxComponent[] {
    const groupCheckbox = this.checkboxes()[0];
    const childCheckboxes = this.getChildCheckboxes(checkboxes);

    if (!this.searchValue()) {
      return childCheckboxes;
    }

    const groupMatches = groupCheckbox
      ?.label()
      ?.toLowerCase()
      .includes(this.searchValue());

    return childCheckboxes.filter(
      (checkbox) =>
        checkbox.isChecked() ||
        groupMatches ||
        checkbox.label()?.toLowerCase().includes(this.searchValue())
    );
  }

  public search(searchValue: string): void {
    this.searchValue.set(searchValue);

    if (!searchValue) {
      this.checkboxes().forEach((checkbox: MultiSelectCheckboxComponent) => {
        checkbox.isVisible.set(true);
      });

      return;
    }

    const groupCheckbox = this.checkboxes()[0];
    if (!groupCheckbox) {
      return;
    }

    const groupMatches = groupCheckbox
      ?.label()
      ?.toLowerCase()
      .includes(searchValue);
    const childCheckboxes = this.checkboxes().slice(1);
    const hasMatchingChild = childCheckboxes.some(
      (checkbox: MultiSelectCheckboxComponent) =>
        checkbox.label()?.toLowerCase().includes(searchValue)
    );
    const hasSelectedChild = childCheckboxes.some(
      (checkbox: MultiSelectCheckboxComponent) => checkbox.isChecked()
    );

    groupCheckbox?.isVisible.set(
      !!groupMatches || hasMatchingChild || hasSelectedChild
    );

    childCheckboxes.forEach((checkbox: MultiSelectCheckboxComponent) => {
      checkbox.isVisible.set(
        checkbox.isChecked() ||
          !!groupMatches ||
          checkbox.label()?.toLowerCase().includes(searchValue)
      );
    });
  }

  public setChildrenChecked(isChecked: boolean): void {
    untracked(() => {
      this.getSearchSelectableChildCheckboxes().forEach(
        (checkbox: MultiSelectCheckboxComponent) =>
          checkbox.isChecked.set(isChecked)
      );
    });
  }

  public updateParentChecked(): void {
    const groupCheckbox = this.checkboxes()[0];
    if (!groupCheckbox) {
      return;
    }

    const childCheckboxes = this.searchValue()
      ? this.getSearchSelectableChildCheckboxes()
      : this.getChildCheckboxes();

    groupCheckbox.isChecked.set(this.isGroupSelected(childCheckboxes));
  }
}
